import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subMonths, format, startOfMonth, endOfMonth } from "date-fns";
import { logger } from "@/lib/logger";

export interface GSCPerformance {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  clicksChange: number;
  impressionsChange: number;
  ctrChange: number;
  positionChange: number;
  loading: boolean;
  hasData: boolean;
  lastSync: string | null;
}

export function useGSCPerformance() {
  const [data, setData] = useState<GSCPerformance>({
    clicks: 0,
    impressions: 0,
    ctr: 0,
    position: 0,
    clicksChange: 0,
    impressionsChange: 0,
    ctrChange: 0,
    positionChange: 0,
    loading: true,
    hasData: false,
    lastSync: null
  });

  useEffect(() => {
    async function fetchGSCData() {
      try {
        // 1. Get the selected property and last sync date
        const { data: settingsList } = await supabase
          .from('gsc_settings')
          .select('selected_property_id, last_performance_sync')
          .order('updated_at', { ascending: false });
        
        const settings = settingsList?.[0];
        if (!settings?.selected_property_id) {
          setData(prev => ({ ...prev, loading: false }));
          return;
        }

        const propertyId = settings.selected_property_id;
        const lastSync = settings.last_performance_sync;

        // 2. Query stored performance data for the last 12 months
        const twelveMonthsAgo = format(subMonths(new Date(), 12), 'yyyy-MM-dd');

        // Fetch current period (last 12 months)
        const { data: currentPeriod, error: currentErr } = await supabase
          .from('gsc_search_performance')
          .select('clicks, impressions, ctr, position')
          .eq('property_id', propertyId)
          .gte('date', twelveMonthsAgo);

        if (currentErr) throw currentErr;

        if (!currentPeriod || currentPeriod.length === 0) {
          setData(prev => ({ ...prev, loading: false, lastSync }));
          return;
        }

        const currentTotals = currentPeriod.reduce((acc, row) => ({
          clicks: acc.clicks + row.clicks,
          impressions: acc.impressions + row.impressions,
          ctr: acc.ctr + row.ctr,
          position: acc.position + row.position
        }), { clicks: 0, impressions: 0, ctr: 0, position: 0 });

        const avgCurrent = {
          clicks: currentTotals.clicks,
          impressions: currentTotals.impressions,
          ctr: currentTotals.ctr / currentPeriod.length,
          position: currentTotals.position / currentPeriod.length
        };

        setData({
          clicks: avgCurrent.clicks,
          impressions: avgCurrent.impressions,
          ctr: avgCurrent.ctr,
          position: avgCurrent.position,
          clicksChange: 0,
          impressionsChange: 0,
          ctrChange: 0,
          positionChange: 0,
          loading: false,
          hasData: true,
          lastSync
        });

      } catch (err) {
        logger.error("Error fetching GSC performance:", err);
        setData(prev => ({ ...prev, loading: false }));
      }
    }

    fetchGSCData();
  }, []);

  return data;
}
